import { Card } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { BOLT, fmtCredits, fmtMoney, fmtUsdt, fmtUsdtFull } from '@/lib/format'

/** GET /accounting/summary → `liabilities`: what the platform owes right now
 *  (docs/accounting.md «Обязательства платформы»). Not tied to the selected period. */
export interface Liabilities {
  /** ⚡ on balances, held by running generations and inside open gifts */
  unspent_credits: string
  /** their RUB value at each user's weighted average price */
  unspent_credits_value_rub: string
  /** part of the value travelling inside gifts not redeemed yet */
  gifts_in_transit_rub: string
  /** partner balances (held + available) and open payout requests */
  referral_payable_usdt: string
  referral_payable_rub: string
  /** ratable subscription access not recognized yet (docs/accounting.md, option «по дням») */
  deferred_revenue_rub?: string
  /** memo, never in profit: the open partner USDT at the ₽ it was booked with and the FX
   *  difference a settlement at today's rate would realize (gain > 0) */
  referral_book_usdt?: string
  referral_book_rub?: string
  referral_fx_unrealized_rub?: string
  total_rub: string
}

export interface LiabilitiesCardProps {
  /** undefined while loading */
  data?: Liabilities
  /** a failed load: the reason in words instead of a busy skeleton */
  error?: unknown
  onRetry?: () => void
}

function Row({ k, v, title, strong }: { k: string; v: string; title?: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className={strong ? 'min-w-0 text-ink-1' : 'min-w-0 text-ink-2'}>{k}</span>
      <span className="tnum shrink-0 text-right text-ink-1" title={title}>
        {v}
      </span>
    </div>
  )
}

/** «Обязательства платформы»: unspent ⚡ are not revenue until consumed, partner money is owed
 *  until paid out. A snapshot as of now, so it says so under the title. */
export function LiabilitiesCard({ data, error, onRetry }: LiabilitiesCardProps) {
  const header = (
    <SectionHeader
      as="h3"
      title="Обязательства платформы"
      description={`На текущий момент, не зависит от выбранного периода. Неизрасходованные ${BOLT} станут выручкой, когда их потратят.`}
    />
  )
  if (data === undefined) {
    return (
      <section>
        {header}
        {error ? (
          <Card>
            <ErrorState title="Не удалось загрузить обязательства" error={error} onRetry={onRetry} />
          </Card>
        ) : (
          <Card>
            <div className="flex flex-col gap-2" role="status" aria-label="Загрузка">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          </Card>
        )}
      </section>
    )
  }
  const inGifts = Number(data.gifts_in_transit_rub) !== 0
  const deferred = data.deferred_revenue_rub !== undefined && Number(data.deferred_revenue_rub) !== 0
  const fx = data.referral_fx_unrealized_rub !== undefined && Number(data.referral_fx_unrealized_rub) !== 0
  return (
    <section>
      {header}
      <Card>
        <div className="flex flex-col gap-2 text-body">
          <Row k={`Неизрасходованные ${BOLT}`} v={fmtCredits(data.unspent_credits, { unit: false })} />
          <Row k="Их стоимость" v={fmtMoney(data.unspent_credits_value_rub, 'RUB')} />
          {inGifts && <Row k="В том числе в неактивированных подарках" v={fmtMoney(data.gifts_in_transit_rub, 'RUB')} />}
          <Row
            k="Партнёрам к выплате"
            v={fmtUsdt(data.referral_payable_usdt)}
            title={fmtUsdtFull(data.referral_payable_usdt)}
          />
          <Row k="Партнёрам к выплате по текущему курсу" v={fmtMoney(data.referral_payable_rub, 'RUB')} />
          {deferred && (
            <Row k="Отложенная выручка по подпискам" v={fmtMoney(data.deferred_revenue_rub ?? '0', 'RUB')} />
          )}
          <div className="mt-1 border-t border-hairline pt-2">
            <Row k="Итого обязательств" v={fmtMoney(data.total_rub, 'RUB')} strong />
          </div>
          {fx && (
            <p className="text-caption text-ink-3">
              Справочно: партнёрские USDT учтены по курсу начисления на {fmtMoney(data.referral_book_rub ?? '0', 'RUB')};
              при выплате по текущему курсу курсовая разница составит{' '}
              <span className="tnum">{fmtMoney(data.referral_fx_unrealized_rub ?? '0', 'RUB')}</span>
              {Number(data.referral_fx_unrealized_rub) > 0 ? ' (доход)' : ' (расход)'}. В прибыль не входит.
            </p>
          )}
        </div>
      </Card>
    </section>
  )
}
